import { SlidersHorizontal, Printer } from "lucide-react";
import { formatDay, addDays } from "@/lib/campaignPlanning";
import { DoneSummary } from "@/components/schools/setup/StepPanel";
import { ActionRow, GuideTip, StepIntro } from "@/components/schools/setup/SetupPrimitives";
import { helpHref } from "@/components/schools/setup/steps";
import NotificationPlanForm from "@/components/schools/NotificationPlanForm";
import { NOTIFICATION_GROUPS } from "@/lib/notificationPlan";

const shortDate = (iso) => new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" });

// ── 9. Teacher info ───────────────────────────────────────────

export function TeacherInfoStep({ step, setup }) {
  const info = setup.pre_campaign_info;
  if (step.status === "blocked" || !info) return null;

  const action = { label: "Customize & download", href: info.teachers_edit_path, icon: SlidersHorizontal };

  return (
    <div className="space-y-6">
      {step.status === "done" ? (
        <DoneSummary title="Downloaded" actions={<ActionRow actions={[{ ...action, primary: false }]} />}>
          Downloaded {shortDate(setup.campaign_details.teacher_info_shared_at)}. Email or print it for your teachers, and send it again any time, for example to a new teacher.
        </DoneSummary>
      ) : (
        <>
          <StepIntro>
            <p>Read it over, add anything of your own, then download the PDF to email or print for your teachers.</p>
          </StepIntro>
          <ActionRow actions={[action]} />
        </>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <GuideTip title="Send it before the take-home sheets">
          Teachers hand out the sheets and field families' first questions, so they should hear about the read-a-thon from you first. A week ahead is plenty.
        </GuideTip>
        <GuideTip title="Point them to the teacher guide" href={helpHref("getting-started-teacher")} linkLabel="Getting started as a teacher">
          A five-minute orientation: signing in, logging class reading, and handing out prizes.
        </GuideTip>
      </div>
    </div>
  );
}

// ── 10. Family info ───────────────────────────────────────────

export function FamilyInfoStep({ step, setup }) {
  const info = setup.pre_campaign_info;
  if (step.status === "blocked" || !info) return null;

  const action = { label: "Customize & download", href: info.families_edit_path, icon: SlidersHorizontal };
  const sendBy = setup.key_dates.sheets_home_on && addDays(setup.key_dates.sheets_home_on, -3);

  return (
    <div className="space-y-6">
      {step.status === "done" ? (
        <DoneSummary title="Downloaded" actions={<ActionRow actions={[{ ...action, primary: false }]} />}>
          Downloaded {shortDate(setup.campaign_details.family_info_downloaded_at)}. Print it and send it home ahead of the take-home sheets.
        </DoneSummary>
      ) : (
        <>
          <StepIntro>
            <p>
              {sendBy && sendBy >= setup.key_dates.today && `Send it home by ${formatDay(sendBy)}, a few days before the take-home sheets. `}
              It fits on one sheet of paper, with a black-and-white version for school printers. Read it over and add a section of your own before you download it.
            </p>
          </StepIntro>
          <ActionRow actions={[action]} />
        </>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <GuideTip title="Say what counts as reading" href="/read-a-thon" linkLabel="From the planning guide">
          Read-alouds? Audiobooks? Reading in a home language? Use the sheet's section of your own to say, so nobody has to guess.
        </GuideTip>
        <GuideTip title="Keep donating optional">
          Make it clear every child can take part in the reading, whether or not their family donates or shares a link.
        </GuideTip>
      </div>
    </div>
  );
}

// ── 11. Take-home sheets ──────────────────────────────────────

export function TakeHomeSheetsStep({ step, setup }) {
  const { counts, paths, key_dates: dates } = setup;
  if (step.status === "blocked") return null;

  const actions = [
    { label: "Customize & download", href: paths.edit_take_home_sheet, icon: SlidersHorizontal },
    {
      label: counts.students === 1
        ? (step.status === "done" ? "Download the sheet again" : "Download the sheet")
        : (step.status === "done" ? `Download all ${counts.students} again` : `Download all ${counts.students} sheets`),
      href: paths.take_home_sheets,
      external: true,
      icon: Printer,
    },
  ];
  const sheetsAhead = dates.sheets_home_on && dates.today <= dates.sheets_home_on;
  const aimFor = !sheetsAhead ? "" : dates.donations_open_any_time
    ? `Aim to have these go home by ${formatDay(dates.sheets_home_on)}, about two weeks before reading starts. `
    : `Aim to have these go home as donations open on ${formatDay(dates.sheets_home_on)}. `;

  return (
    <div className="space-y-6">
      {step.status === "done" ? (
        <DoneSummary title="Sheets downloaded" actions={<ActionRow actions={actions.map((action) => ({ ...action, primary: false }))} />}>
          Downloaded {shortDate(setup.campaign_details.take_home_sheets_downloaded_at)}. Split the stack by classroom for teachers to hand out. Reprint just the classrooms where new students join.
        </DoneSummary>
      ) : (
        <>
          <StepIntro>
            <p>
              {aimFor}
              Families can activate right away and start sharing their page with relatives.
            </p>
          </StepIntro>
          <ActionRow actions={actions} />
        </>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <GuideTip title="One PDF, the whole school">
          Grouped by classroom and ready to split into stacks. Plain paper and black and white are fine. Teachers can also print their own class from their dashboard.
        </GuideTip>
        <GuideTip title="A lost sheet is easy to replace" href={helpHref("rotating-activation-codes")} linkLabel="Replacing a lost sheet">
          Swap the student's code for a new one and print a fresh sheet. Families who already activated keep their access.
        </GuideTip>
      </div>
    </div>
  );
}

// ── 12. Emails ────────────────────────────────────────────────

export function NotificationsStep({ step, setup }) {
  const { notifications, paths } = setup;
  const total = NOTIFICATION_GROUPS.reduce((n, g) => n + g.emails.length, 0);

  return (
    <div className="space-y-6">
      {step.status === "done" && (
        <DoneSummary title={`${notifications.enabled.length} of ${total} emails on`}>
          Saved {shortDate(notifications.reviewed_at)}. Change it here or in Settings any time.
        </DoneSummary>
      )}
      <NotificationPlanForm
        plan={notifications}
        path={paths.update_notifications}
        returnTo={{ return_to: "setup", campaign: setup.campaign?.slug || "" }}
        submitLabel={step.status === "done" ? "Save changes" : "Save email plan"}
      />
      <GuideTip title="Sending your own updates instead?">
        Some schools prefer to send progress updates in their own newsletter. If you switch a PagePledge email off, make sure your own message covers it — especially the “wrapping up” reminder, the last chance to log minutes.
      </GuideTip>
    </div>
  );
}
